/**
 * Advertisers API Service
 * Handles all advertiser-related API calls with role-based routing
 */

import { apiClient } from './client'
import { getStoredRole } from '../../utils/token'

export interface Advertiser {
  id: number
  name: string
  time_zone?: string
  email?: string
  company_name?: string
  status?: string
  created_at?: string
  updated_at?: string
}

export interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page?: number
}

interface AdvertisersListResponse {
  advertisers: Advertiser[]
  meta: PaginationMeta
}

export interface AdvertisersQueryParams {
  page?: number
  per_page?: number
  q?: string
}

/**
 * Get the base path for advertisers based on user role
 */
function getAdvertisersBasePath(): string {
  const role = getStoredRole()
  return role === 'admin'
    ? '/administrator/advertisers'
    : '/advertisers'
}

/**
 * Build query string from params
 */
function buildQueryString(params: AdvertisersQueryParams): string {
  const queryParams = new URLSearchParams()
  if (params.page) queryParams.append('page', params.page.toString())
  if (params.per_page) queryParams.append('per_page', params.per_page.toString())
  if (params.q) queryParams.append('q', params.q)
  const queryString = queryParams.toString()
  return queryString ? `?${queryString}` : ''
}

export const advertiserApi = {
  /**
   * Get advertisers for the current user with pagination and search
   */
  getAdvertisers: async (params: AdvertisersQueryParams = {}): Promise<{ advertisers: Advertiser[], meta: PaginationMeta }> => {
    const basePath = getAdvertisersBasePath()
    const queryString = buildQueryString(params)
    const response = await apiClient.get<AdvertisersListResponse>(
      `${basePath}${queryString}`
    )
    return {
      advertisers: response.advertisers || [],
      meta: response.meta || { current_page: 1, total_pages: 1, total_count: 0 }
    }
  },

  /**
   * Get a specific advertiser by ID
   */
  getAdvertiser: async (id: number): Promise<Advertiser> => {
    const basePath = getAdvertisersBasePath()
    const response = await apiClient.get<{ advertiser: Advertiser }>(
      `${basePath}/${id}`
    )
    return response.advertiser
  },
}

